import { useMemo } from 'react';
import { Content } from 'src/contexts/ContentContext';
import { useEventMapContext } from 'src/contexts/EventMapContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ReportMapIssueButton } from 'src/modals/VenueMapReportModal';
import {
  VenueMapConfigPlaceholder,
  VenueMapUrl,
  VenueMapV3Url,
  VenueMapVersionPlaceholder,
} from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';
import { Feature, SectionInfo } from 'src/WebApiController';

import { useCtrlPlusM } from './EventVenueMap.hooks';
import { VenueMapV3 } from './v3/VenueMapV3';
import { VenueMap } from './VenueMap';
import * as styles from './VenueMap.css';
import { VenueMapProps } from './VenueMapProps';

export type EventVenueMapProps = Omit<
  VenueMapProps,
  | 'selectedSections'
  | 'svgMapUrl'
  | 'venueConfigId'
  | 'availableSections'
  | 'focalPoint'
> & {
  selectedSectionIds?: number[];
  onToggleMirrors?: (sections: SectionInfo[], exclude?: boolean) => void;
  /**
   * Show the "report map issue" action overlay (POS-9306). Off by default so it
   * only appears where intended (the inventory event MapBody and the create-PO
   * modal map), not on every venue map surface.
   */
  showReportMapIssue?: boolean;
};

export const EventVenueMap = ({
  selectedSectionIds,
  markedSectionIds,
  animatedSectionIds,
  onSectionClicked,
  onSectionHovered,
  onSectionTapAndHold,
  setSelectedSections,
  onToggleMirrors,
  staticInfoContent,
  staticInfoContentPosition,
  alwaysShowStaticInfo,
  infoIconContent,
  isZoomEnabled = true,
  hideControls = false,
  isPanEnabled = true,
  showDefaultMapColors,
  colorBandProps,
  getColor,
  useRowMap = false,
  isHeatMap = false,
  isWhiteBackground = false,
  isRelocatedStaticInfo = false,
  allowDrawLine = true,
  isCondensedView = false,
  cornerContent,
  statsContent,
  disableHoverEffect = false,
  mapActionContainerClassname,
  condensedControl = false,
  showReportMapIssue = false,
}: EventVenueMapProps) => {
  const { venueMapInfo, event, isMapLoading, isGeneralAdmissionOnly } =
    useEventMapContext();
  const hasCatalogSupportFeature = useUserHasFeature(Feature.CatalogSupport);

  useCtrlPlusM(markedSectionIds ?? selectedSectionIds ?? [], onToggleMirrors);

  const selectedSectionData = useMemo(() => {
    if (!venueMapInfo || !selectedSectionIds?.length) {
      return undefined;
    }

    return venueMapInfo.sections?.filter((s: SectionInfo) =>
      selectedSectionIds.includes(s.id)
    );
  }, [selectedSectionIds, venueMapInfo]);

  const shouldUseV3 = venueMapInfo?.hasRow;

  if (isMapLoading) {
    return (
      <div className={styles.venueMapUnavailableContainer}>
        <PosSpinner />
      </div>
    );
  }
  if (!venueMapInfo) {
    return (
      <div className={styles.venueMapUnavailableContainer}>
        <Content id={ContentId.MapUnavailable} />
      </div>
    );
  }

  return (
    <div className={styles.eventVenueMapContainer}>
      {shouldUseV3 ? (
        <VenueMapV3
          svgMapUrl={VenueMapV3Url.replace(
            VenueMapConfigPlaceholder,
            venueMapInfo.venueCfgId.toString()
          ).replace(
            VenueMapVersionPlaceholder,
            (venueMapInfo.svgMapVer ?? 1).toString()
          )}
          useRowMap={useRowMap}
          venueConfigId={venueMapInfo.venueCfgId}
          focalPoint={venueMapInfo.focalPt}
          availableSections={venueMapInfo.sections ?? []}
          selectedSections={selectedSectionData}
          markedSectionIds={markedSectionIds}
          animatedSectionIds={animatedSectionIds}
          onSectionClicked={onSectionClicked}
          onSectionTapAndHold={onSectionTapAndHold}
          onSectionHovered={onSectionHovered}
          setSelectedSections={setSelectedSections}
          staticInfoContent={staticInfoContent}
          staticInfoContentPosition={staticInfoContentPosition}
          alwaysShowStaticInfo={alwaysShowStaticInfo}
          infoIconContent={infoIconContent}
          isGeneralAdmissionOnly={isGeneralAdmissionOnly}
          showDefaultMapColors={showDefaultMapColors}
          colorBandProps={colorBandProps}
          isZoomEnabled={isZoomEnabled}
          hideControls={hideControls}
          isPanEnabled={isPanEnabled}
          isWhiteBackground={isWhiteBackground}
          isRelocatedStaticInfo={isRelocatedStaticInfo}
          getColor={getColor}
          isHeatMap={isHeatMap}
          cornerContent={cornerContent}
          statsContent={statsContent}
          allowDrawLine={allowDrawLine}
          disableHoverEffect={disableHoverEffect}
          isCondensedView={isCondensedView}
          mapActionContainerClassname={mapActionContainerClassname}
          condensedControl={condensedControl}
        />
      ) : (
        <VenueMap
          svgMapUrl={VenueMapUrl.replace(
            VenueMapConfigPlaceholder,
            venueMapInfo.venueCfgId.toString()
          ).replace(
            VenueMapVersionPlaceholder,
            (venueMapInfo.svgMapVer ?? 1).toString()
          )}
          venueConfigId={venueMapInfo.venueCfgId}
          focalPoint={venueMapInfo.focalPt}
          availableSections={venueMapInfo.sections ?? []}
          selectedSections={selectedSectionData}
          markedSectionIds={markedSectionIds}
          animatedSectionIds={animatedSectionIds}
          onSectionClicked={onSectionClicked}
          onSectionTapAndHold={onSectionTapAndHold}
          onSectionHovered={onSectionHovered}
          setSelectedSections={setSelectedSections}
          staticInfoContent={staticInfoContent}
          staticInfoContentPosition={staticInfoContentPosition}
          infoIconContent={infoIconContent}
          isGeneralAdmissionOnly={isGeneralAdmissionOnly}
          showDefaultMapColors={showDefaultMapColors}
          colorBandProps={colorBandProps}
          isZoomEnabled={isZoomEnabled}
          hideControls={hideControls}
          isPanEnabled={isPanEnabled}
          isWhiteBackground={isWhiteBackground}
          isRelocatedStaticInfo={isRelocatedStaticInfo}
          isCondensedView={isCondensedView}
          mapActionContainerClassname={mapActionContainerClassname}
          getColor={getColor}
          isHeatMap={isHeatMap}
          cornerContent={cornerContent}
          statsContent={statsContent}
          allowDrawLine={allowDrawLine}
          disableHoverEffect={disableHoverEffect}
          condensedControl={condensedControl}
        />
      )}
      {/* Report-map-issue action, overlaid like the other map action icons.
          Only shown where opted in (inventory event MapBody and the create-PO
          modal map) and behind the catalog-support feature — POS-9306. The
          button further self-gates on the inventory-edit permission. */}
      {showReportMapIssue && hasCatalogSupportFeature && (
        <div className={styles.reportMapIssueOverlay}>
          <ReportMapIssueButton
            eventId={event?.viagId}
            venueConfigId={venueMapInfo.venueCfgId}
          />
        </div>
      )}
    </div>
  );
};
